// src/pages/admin/CancellationFeedbackPage.js — admin view of the
// cancellation survey responses stored by set_subscription_cancel.php.
import React, { useState, useEffect, useCallback } from 'react';
import { CANCEL_REASONS } from '../../components/billing/CancelSurveyModal';

const API_BASE = `${process.env.REACT_APP_API_URL}/api`;

const REASON_LABELS = Object.fromEntries(CANCEL_REASONS.map(({ value, label }) => [value, label]));

const CancellationFeedbackPage = () => {
    const [rows, setRows] = useState([]);
    const [counts, setCounts] = useState({});
    const [loadError, setLoadError] = useState('');
    const [loaded, setLoaded] = useState(false);

    const fetchFeedback = useCallback(async () => {
        setLoadError('');
        try {
            const res = await fetch(`${API_BASE}/admin_list_cancellation_feedback.php`, { credentials: 'include' });
            const data = await res.json();
            if (res.ok && data.status === 'success') {
                setRows(data.feedback);
                setCounts(data.counts || {});
            } else {
                setLoadError(data.message || 'Failed to load feedback.');
            }
        } catch (err) {
            setLoadError('Network error loading feedback.');
        } finally {
            setLoaded(true);
        }
    }, []);

    useEffect(() => { fetchFeedback(); }, [fetchFeedback]);

    const sortedCounts = Object.entries(counts).sort((a, b) => b[1] - a[1]);

    return (
        <div className="px-4 py-10 text-white">
            <div className="max-w-4xl mx-auto">
                <header className="mb-8">
                    <h1 className="text-3xl font-bold tracking-tight">Cancellation Feedback</h1>
                    <p className="text-sm text-neutral-400 mt-1">Survey answers collected when members cancel.</p>
                </header>

                {loadError && <p className="text-red-400 text-sm mb-4">{loadError}</p>}

                {sortedCounts.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-6">
                        {sortedCounts.map(([reason, n]) => (
                            <span key={reason} className="px-3 py-1 rounded-full text-sm bg-white/5 border border-white/10 text-neutral-300">
                                {REASON_LABELS[reason] || reason} <span className="text-cyan-300 font-semibold ml-1">{n}</span>
                            </span>
                        ))}
                    </div>
                )}

                {loaded && !loadError && rows.length === 0 && (
                    <p className="text-neutral-400 text-sm">No cancellations recorded yet.</p>
                )}

                {rows.length > 0 && (
                    <div className="overflow-x-auto rounded-2xl border border-white/10">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-neutral-500 border-b border-white/10 bg-white/[0.03]">
                                    <th className="py-2.5 px-4 font-medium">Date</th>
                                    <th className="py-2.5 px-4 font-medium">User</th>
                                    <th className="py-2.5 px-4 font-medium">Status</th>
                                    <th className="py-2.5 px-4 font-medium">Reason</th>
                                    <th className="py-2.5 px-4 font-medium">Comment</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={row.id} className="border-b border-white/5 last:border-0 align-top">
                                        <td className="py-2.5 px-4 text-neutral-400 whitespace-nowrap">{String(row.created_at).split(' ')[0]}</td>
                                        <td className="py-2.5 px-4">{row.email}</td>
                                        <td className="py-2.5 px-4 text-neutral-400">{row.subscription_status || '—'}</td>
                                        <td className="py-2.5 px-4">{REASON_LABELS[row.reason] || row.reason}</td>
                                        <td className="py-2.5 px-4 text-neutral-300 max-w-md">{row.comment || <span className="text-neutral-600">—</span>}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CancellationFeedbackPage;
